import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from '../../assets/icons';
import { useCompany } from '../../hooks/useCompany';
import { useFetchLicenseListQuery } from '../../services/license.api';
import { SCOPE_TYPE } from '../../services/utils';
import { LICENSE_STATUS } from '../../stores/types/licenses.interface';
import { Button, ButtonSize, ButtonType, MODAL_TYPES, useGlobalModalContext } from '../_ui';
import { LicenseAlign } from './LicenseAlign.component';

const AlignLicenses = () => {
  const { t } = useTranslation();
  const { showModal } = useGlobalModalContext();
  const { userScope } = useCompany();

  const { entities } = useFetchLicenseListQuery(
    { scope: userScope || SCOPE_TYPE.COMPANY },
    {
      selectFromResult: (endPoint) => ({
        entities: endPoint.data?.entities ?? [],
      }),
    },
  );

  const eligibleLicenses = useMemo(
    () =>
      entities.filter(
        (l) =>
          (l.status === LICENSE_STATUS.ACTIVE || l.status === LICENSE_STATUS.EXPIRED) &&
          !l.includes4G,
      ),
    [entities],
  );

  const eligibleLicenseIds = useMemo(
    () => eligibleLicenses.map((l) => l.licenseId),
    [eligibleLicenses],
  );

  const isDisabled = useMemo(() => {
    if (eligibleLicenses.length <= 1) return true;
    const dates = eligibleLicenses.map((l) => l.expirationDate).filter((d): d is Date => d != null);
    if (dates.length <= 1) return true;
    const first = new Date(dates[0]).toDateString();
    return dates.every((d) => new Date(d).toDateString() === first);
  }, [eligibleLicenses]);

  const handleAlignClick = () => {
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('license_align_title'),
      width: '540px',
      height: 'max-content',
      onRenderBody: () => <LicenseAlign licenseIds={eligibleLicenseIds} />,
      shouldCloseOnOverlayClick: false,
    });
  };

  return (
    <Button
      icon={Calendar}
      onClick={handleAlignClick}
      label={t('license_align_button')}
      size={ButtonSize.SMALL}
      type={ButtonType.TERTIARY}
      iconAlt={t('license_align_title')}
      disabled={isDisabled}
    />
  );
};

export default AlignLicenses;
